import { useState } from "react";
import { useTranslation } from "react-i18next";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import Field from "../../../ui/forms/Field";
import ModalShell from "../../../ui/overlays/ModalShell";
import { FORM_CONTROL_CLASS } from "../../../ui/styles/formStyles";

interface ReasonDialogProps {
  title: string;
  /** Applies the change with the reason; the dialog closes once it succeeds. */
  onConfirm: (reason: string) => Promise<void>;
  onClose: () => void;
}

/**
 * Asks why before the server's keys are switched on for a group: that spends money, so the
 * server records a reason (accounts.md GS-4, Q-SK-1).
 */
export default function ReasonDialog({ title, onConfirm, onClose }: ReasonDialogProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const { state, submit } = useSubmission();
  const pending = state.status === "submitting";
  return (
    <ModalShell
      title={title}
      onClose={onClose}
      closeDisabled={pending}
      size="md"
      zIndexClassName="z-[60]"
      footer={
        <div className="flex justify-end gap-2">
          <AppButton variant="secondary" size="sm" disabled={pending} onClick={onClose}>{t("common.cancel")}</AppButton>
          <AppButton variant="primary" size="sm" disabled={pending || reason.trim() === ""}
            onClick={() => { submit(async () => { await onConfirm(reason.trim()); onClose(); }); }}>
            {t("access.reason.confirm")}
          </AppButton>
        </div>
      }
    >
      <div className="space-y-3 text-sm">
        <Field label={t("common.settings.reason")}>
          {(control) => (
            <textarea {...control} className={FORM_CONTROL_CLASS} rows={2} value={reason} required disabled={pending}
              onChange={(event) => { setReason(event.target.value); }} />
          )}
        </Field>
        {state.status === "failed" ? <ErrorNotice error={state.error} compact={true} /> : null}
      </div>
    </ModalShell>
  );
}
